import { useCallback, useEffect, useRef, useState } from "react";

import { useAudioContext } from "@/components/providers/audio-context-provider";

const defaultStreamUrl = import.meta.env.DEV
  ? "http://localhost:8080/stream"
  : "https://oscarradio.radioca.st/stream";

export const STREAM_URL = import.meta.env.VITE_STREAM_URL ?? defaultStreamUrl;

/**
 * Owns the <audio> element for the live stream. Attach `audioRef` to an
 * <audio> tag and call `toggle()` from whatever control is driving playback —
 * the hero play button on the homepage, the play bar everywhere else.
 */
export function useRadioStream(options?: {
  playing?: boolean;
  setPlaying?(newVal: boolean): void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(options?.playing ?? false);
  const [volume, setVolume] = useState(75);
  const { connectAudioElement, resume } = useAudioContext();

  const onPlayingChange = options?.setPlaying;
  const externalPlaying = options?.playing;

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) {
      return;
    }

    return connectAudioElement(audioElement);
  }, [connectAudioElement]);

  useEffect(() => {
    if (externalPlaying === undefined) {
      return;
    }

    setPlaying(externalPlaying);

    const audioElement = audioRef.current;

    if (!audioElement) {
      return;
    }

    if (externalPlaying) {
      audioElement.play();
    } else {
      audioElement.pause();
    }
  }, [externalPlaying]);

  const toggle = useCallback(async () => {
    const audioElement = audioRef.current;

    if (!audioElement) {
      return;
    }

    if (audioElement.paused || audioElement.ended) {
      await resume();

      if (!audioElement.getAttribute("src")) {
        audioElement.setAttribute("src", STREAM_URL);
        audioElement.load();
      }

      await audioElement.play();

      setPlaying(true);
      onPlayingChange?.(true);
    } else {
      // Dropping the src rather than just pausing stops us buffering a live
      // stream in the background while nobody is listening.
      audioElement.setAttribute("src", "");
      audioElement.pause();
      setTimeout(() => audioElement.load(), 0);
      setPlaying(false);
      onPlayingChange?.(false);
    }
  }, [onPlayingChange, resume]);

  const changeVolume = useCallback((value: number[]) => {
    if (!audioRef.current) {
      return;
    }

    setVolume(value[0]);
    audioRef.current.volume = value[0] / 100;
  }, []);

  return { audioRef, changeVolume, playing, toggle, volume };
}
